/**
 * Which graph you are in, and how to make another.
 *
 * Graphs are independent: separate model, separate rules, separate records,
 * separate tables. The id is shown and copyable because it is what every API
 * call and every stored row is keyed by.
 */

import { useState } from 'react'
import { CopyButton, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field } from '../components'
import { GenerateDialog } from '../generate/GenerateDialog'
import { useCreateGraph } from '../hooks'
import type { Graph } from '../types/brain.types'

export function GraphSwitcher({
  graphs,
  graphId,
  onSwitch,
}: {
  graphs: Graph[]
  graphId: string
  onSwitch: (id: string) => void
}) {
  const graph = graphs.find((g) => g.graph_id === graphId)
  const [creating, setCreating] = useState<'closed' | 'blank' | 'generate'>('closed')

  return (
    <>
      <select
        className="select cp-graphpicker"
        value={graphId}
        aria-label="Graph"
        onChange={(e) => {
          if (e.target.value === '__new') setCreating('blank')
          else if (e.target.value === '__generate') setCreating('generate')
          else onSwitch(e.target.value)
        }}
      >
        {graphs.map((g) => (
          <option key={g.graph_id} value={g.graph_id}>
            {g.name}
            {g.node_type_count !== undefined ? ` · ${g.node_type_count} types` : ''}
          </option>
        ))}
        <option disabled>──────────</option>
        <option value="__new">+ New empty graph…</option>
        <option value="__generate">+ Generate a graph…</option>
      </select>

      {graph && (
        <span className="row-tight hide-narrow" title={`graph_id ${graph.graph_id}`}>
          <code className="mono faint" style={{ fontSize: 10.5 }}>
            {graph.graph_id.slice(0, 8)}
          </code>
          <CopyButton value={graph.graph_id} label="id" />
        </span>
      )}

      {creating === 'blank' && (
        <NewGraphDialog
          onClose={() => setCreating('closed')}
          onCreated={(id) => {
            setCreating('closed')
            onSwitch(id)
          }}
        />
      )}
      {creating === 'generate' && (
        <GenerateDialog
          onClose={() => setCreating('closed')}
          onDone={(id) => {
            setCreating('closed')
            onSwitch(id)
          }}
        />
      )}
    </>
  )
}

function NewGraphDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (graphId: string) => void
}) {
  const create = useCreateGraph()
  const toast = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  return (
    <Modal
      title="New graph"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={create.isPending || !name.trim()}
            onClick={async () => {
              try {
                const graph = await create.mutateAsync({
                  name: name.trim(),
                  description: description.trim() || undefined,
                })
                toast.success(`Created ${graph.name}.`)
                onCreated(graph.graph_id)
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          A graph is self-contained: its own node types, rules, records and tables. Nothing is
          shared with your other graphs, so this is the right unit for a second domain — or for
          trying something out without disturbing what works.
        </p>
        <Field label="Name" required>
          <input
            className="input"
            value={name}
            placeholder="Supply chain"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="What is it for?">
          <input
            className="input"
            value={description}
            placeholder="Suppliers, parts and the incidents raised against them."
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  )
}
